import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Trash2, ArrowLeft, MessageCircle, Pin } from "lucide-react";
import { toast } from "sonner";

const REACTIONS = [
  { key: "like", emoji: "👍", label: "Like" },
  { key: "dislike", emoji: "👎", label: "Dislike" },
  { key: "laugh", emoji: "😂", label: "Laugh" },
  { key: "rocket", emoji: "🚀", label: "Rocket" },
  { key: "fire", emoji: "🔥", label: "Fire" },
];

function PostMeta({ post }) {
  useEffect(() => {
    if (!post) return;
    const siteName = "MediaCrypto";
    const title = post.title + " | " + siteName + " Community";
    const description = post.content.slice(0, 160).replace(/\n/g, " ") + "...";
    const url = "https://mediacrypto.ai/community/" + post.id;
    document.title = title;
    const setMeta = (selector, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const attr = selector.includes("[property") ? "property" : "name";
        const key = selector.match(/["']([^"']+)["']/)[1];
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[property="og:type"]', "article");
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="twitter:card"]', "summary");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);
    return () => {
      document.title = "MediaCrypto — Real-Time Crypto News, Prices & Analysis";
      setMeta('meta[name="description"]', "MediaCrypto delivers real-time cryptocurrency news, live Bitcoin and Ethereum prices, market analysis, and community discussion. Stay ahead of the crypto market.");
      setMeta('meta[property="og:title"]', "MediaCrypto — Real-Time Crypto News, Prices & Analysis");
      setMeta('meta[property="og:description"]', "MediaCrypto delivers real-time cryptocurrency news, live Bitcoin and Ethereum prices, market analysis, and community discussion.");
      setMeta('meta[property="og:type"]', "website");
      setMeta('meta[property="og:url"]', "https://mediacrypto.ai/");
      canonical.setAttribute("href", "https://mediacrypto.ai/");
    };
  }, [post]);
  return null;
}

export default function CommunityPostDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reactionCounts, setReactionCounts] = useState({});
  const [userReaction, setUserReaction] = useState(null);
  const [showReactions, setShowReactions] = useState(false);
  const reactionRef = useRef(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/community/posts/" + id),
        api.get("/community/posts/" + id + "/comments"),
      ]);
      setPost(a.data);
      setComments(b.data);
    } catch (_) {
      toast.error("Could not load post");
    } finally {
      setLoading(false);
    }
  };

  const loadReactions = async () => {
    try {
      const { data } = await api.get("/community/posts/" + id + "/reactions");
      setReactionCounts(data.counts || {});
      setUserReaction(data.user_reaction || null);
    } catch (_) {}
  };

  useEffect(() => { load(); loadReactions(); }, [id, user?.id]);

  useEffect(() => {
    const handleClick = (e) => {
      if (reactionRef.current && !reactionRef.current.contains(e.target)) setShowReactions(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const react = async (reactionKey) => {
    if (!user) return toast.error("Sign in to react");
    try {
      const { data } = await api.post("/community/posts/" + id + "/react", { reaction: reactionKey });
      setReactionCounts(data.counts || {});
      setUserReaction(data.user_reaction);
      setShowReactions(false);
    } catch (_) { toast.error("Could not react"); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/community/posts/" + id + "/comments", { content: text });
      setText("");
      toast.success("Comment posted");
      load();
    } catch (_) {
      toast.error("Could not post comment");
    } finally { setSubmitting(false); }
  };

  const removeComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete("/comments/" + cid);
      toast.success("Comment deleted");
      load();
    } catch (_) { toast.error("Could not delete comment"); }
  };

  useEffect(() => {
    if (loading || post) return;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [loading, post]);

  if (loading) return <div className="pt-32 text-center text-[var(--text-tertiary)] font-mono">Loading post...</div>;
  if (!post) return <div className="pt-32 text-center text-[var(--text-tertiary)] font-mono">Post not found.</div>;

  const totalReactions = Object.values(reactionCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="pt-10 pb-20 max-w-3xl mx-auto px-6">
      <PostMeta post={post} />
      <Link to="/community" className="inline-flex items-center gap-2 text-sm font-mono text-[var(--text-tertiary)] hover:text-yellow-400 transition mb-8" data-testid="back-to-community">
        <ArrowLeft className="w-4 h-4" /> Back to feed
      </Link>

      <article className={"bg-[var(--bg-panel)] border p-8 " + (post.pinned ? "border-yellow-400/40" : "border-[var(--border-soft)]")} data-testid="community-post-detail">
        {post.pinned && (
          <div className="flex items-center gap-1 text-yellow-400 text-xs font-mono uppercase tracking-widest mb-4">
            <Pin className="w-3 h-3" /> Pinned
          </div>
        )}

        <div className="flex items-center gap-3 mb-6">
          <Link to={"/profile/" + post.author_id} className="flex-shrink-0 hover:opacity-80 transition">
  {post.author_avatar ? (
    <img src={post.author_avatar} alt={post.author_name} className="w-10 h-10 rounded-full object-cover" />
  ) : (
    <div className="w-10 h-10 bg-yellow-400 text-black font-bold flex items-center justify-center text-sm rounded-full">
      {(post.author_name || "U")[0].toUpperCase()}
    </div>
  )}
</Link>
          <div>
            <Link to={"/profile/" + post.author_id} className="text-sm font-medium text-[var(--text-primary)] hover:text-yellow-400 transition">
  {post.author_name}
</Link>
            <div className="text-xs font-mono text-[var(--text-tertiary)]">
              {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </div>
          </div>
        </div>

        <h1 className="font-display font-bold text-3xl md:text-4xl text-[var(--text-primary)] tracking-tight">{post.title}</h1>
        <p className="mt-6 text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap break-all">{post.content}</p>

        <div className="mt-8 pt-6 border-t border-[var(--border-soft)] flex items-center gap-4 flex-wrap">
          <div ref={reactionRef} className="relative">
            <button
              onClick={() => setShowReactions(!showReactions)}
              className={"flex items-center gap-2 transition px-3 py-1.5 border rounded-full text-sm " +
                (userReaction ? "border-yellow-400/40 text-yellow-400" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400/30 hover:text-[var(--text-primary)]")}
            >
              <span>{userReaction ? REACTIONS.find((r) => r.key === userReaction)?.emoji : "😊"}</span>
              <span className="font-mono">{totalReactions > 0 ? totalReactions : "React"}</span>
            </button>
            {showReactions && (
              <div className="absolute bottom-full left-0 mb-2 bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-full px-3 py-2 flex items-center gap-2 z-10 shadow-xl">
                {REACTIONS.map((r) => (
                  <button key={r.key} onClick={() => react(r.key)} title={r.label}
                    className={"text-xl hover:scale-125 transition-transform " + (userReaction === r.key ? "opacity-100 scale-125" : "opacity-70 hover:opacity-100")}>
                    {r.emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          {Object.entries(reactionCounts).map(([key, count]) => {
            if (!count) return null;
            const reaction = REACTIONS.find((r) => r.key === key);
            if (!reaction) return null;
            return (
              <span key={key} className="text-sm font-mono text-[var(--text-tertiary)] flex items-center gap-1">
                {reaction.emoji} {count}
              </span>
            );
          })}

          <div className="flex items-center gap-2 text-[var(--text-tertiary)] ml-auto">
            <MessageCircle className="w-5 h-5" />
            <span className="font-mono text-sm">{comments.length} comments</span>
          </div>
        </div>
      </article>

      <section className="mt-10">
        <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--text-tertiary)] mb-6">
          {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
        </h3>

        {user ? (
          <form onSubmit={submitComment} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5 mb-6" data-testid="comment-form">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Add your thoughts..."
              rows={3}
              data-testid="comment-input"
              className="w-full bg-transparent text-[var(--text-secondary)] placeholder:text-[var(--text-tertiary)] resize-none focus:outline-none"
            />
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-[var(--border-soft)]">
              <span className="text-xs font-mono text-[var(--text-tertiary)]">Posting as <span className="text-[var(--text-secondary)]">{user.name}</span></span>
              <button type="submit" disabled={submitting || !text.trim()} data-testid="comment-submit"
                className="bg-yellow-400 text-black font-bold uppercase tracking-wide px-4 py-2 text-sm hover:bg-yellow-500 transition disabled:opacity-50">
                {submitting ? "Posting..." : "Post Comment"}
              </button>
            </div>
          </form>
        ) : (
          <div className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5 mb-6 text-center">
            <p className="text-sm text-[var(--text-tertiary)]">
              <Link to="/login" className="text-yellow-400 hover:underline">Sign in</Link> to leave a comment.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {comments.length === 0 ? (
            <p className="text-[var(--text-tertiary)] font-mono text-sm text-center py-8 border border-[var(--border-soft)] bg-[var(--bg-panel)]">
              No comments yet. Be the first.
            </p>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] p-5" data-testid={"comment-" + c.id}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-[var(--bg-elevated)] text-[var(--text-secondary)] font-bold flex items-center justify-center text-xs">
                      {(c.author_name || "U")[0].toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-[var(--text-primary)]">{c.author_name}</span>
                    <span className="text-xs font-mono text-[var(--text-tertiary)]">
                      · {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  </div>
                  {user && (user.id === c.author_id || user.role === "admin") && (
                    <button onClick={() => removeComment(c.id)} data-testid={"comment-delete-" + c.id} className="text-[var(--text-tertiary)] hover:text-red-400 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-[var(--text-secondary)] leading-relaxed pl-9 break-all">{c.content}</p>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}